import { getP } from 'redux-polyglot'
import api from '../libs/api'
import { getCategories } from '../selectors/video-category'

/**
 * Actions for initializing the application
 */
export const INIT_APP_REQUEST = 'VIDEO_CATEGORY/INIT_APP_REQUEST'

const initAppRequest = () => ({
  type: INIT_APP_REQUEST,
})

export const initApp = categoryId => (dispatch, getState) => {
  dispatch(initAppRequest())
  dispatch(loadCategoryParents(categoryId)).then(() =>
    dispatch(loadCategoriesAndVideos(categoryId)).then(() => {
      const categories = getCategories(getState())
      let categoryVideosCount = 0
      categories.some(category => {
        dispatch(loadCategoryVideos(category.id))
        categoryVideosCount++
        return categoryVideosCount > 9
      })
    })
  )
}

/**
 * Actions for dismissing an error
 */
export const DISMISS_ERROR = 'VIDEO_CATEGORY/DISMISS_ERROR'

export const dismissError = () => ({
  type: DISMISS_ERROR,
})

/**
 * Actions for fetching category parents from API
 */
export const FETCH_CATEGORY_PARENTS_REQUEST =
  'VIDEO_CATEGORY/FETCH/CATEGORY_PARENTS/REQUEST'
export const FETCH_CATEGORY_PARENTS_SUCCESS =
  'VIDEO_CATEGORY/FETCH/CATEGORY_PARENTS/SUCCESS'
export const FETCH_CATEGORY_PARENTS_FAILURE =
  'VIDEO_CATEGORY/FETCH/CATEGORY_PARENTS/FAILURE'

const fetchCategoryParentsRequest = () => ({
  type: FETCH_CATEGORY_PARENTS_REQUEST,
})

const fetchCategoryParentsSuccess = payload => ({
  type: FETCH_CATEGORY_PARENTS_SUCCESS,
  payload,
})

const fetchCategoryParentsFailure = error => ({
  type: FETCH_CATEGORY_PARENTS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadCategoryParents = categoryId => (dispatch, getState) => {
  dispatch(fetchCategoryParentsRequest())
  return api
    .getCategoryParents(categoryId)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const categories = []
          response.categories.forEach(category => {
            const processed = {
              id: category.id,
              name: category.name,
            }
            categories.push(processed)
          })
          return dispatch(fetchCategoryParentsSuccess(categories))
        } else {
          return dispatch(fetchCategoryParentsFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchCategoryParentsFailure(
            getP(getState()).t('tv_video_library.category_parents_loading_error', {
              _: 'An error occurred while loading the video category details',
            })
          )
        )
    )
}

/**
 * Actions for fetching categories from API
 */
export const FETCH_CATEGORIES_REQUEST = 'VIDEO_CATEGORY/FETCH/CATEGORIES/REQUEST'
export const FETCH_CATEGORIES_SUCCESS = 'VIDEO_CATEGORY/FETCH/CATEGORIES/SUCCESS'
export const FETCH_CATEGORIES_FAILURE = 'VIDEO_CATEGORY/FETCH/CATEGORIES/FAILURE'

const fetchCategoriesRequest = () => ({
  type: FETCH_CATEGORIES_REQUEST,
})

const fetchCategoriesSuccess = payload => ({
  type: FETCH_CATEGORIES_SUCCESS,
  payload,
})

const fetchCategoriesFailure = error => ({
  type: FETCH_CATEGORIES_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadCategoriesAndVideos = categoryId => (dispatch, getState) => {
  dispatch(fetchCategoriesRequest())
  dispatch(fetchVideosRequest())
  return api
    .getCategoryItems(categoryId)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const categories = []
          response.categories.forEach(category => {
            const processed = {
              id: category.id,
              name: category.name,
              img: category.img.medium,
            }
            categories.push(processed)
          })
          const videos = []
          response.videos.forEach(video => {
            const processed = {
              id: video.id,
              name: video.name,
              desc: video.description,
              img: video.img.medium,
              duration: video.duration,
              created: video.created,
              isPremium: !!video.is_premium,
            }
            videos.push(processed)
          })
          return Promise.all([
            dispatch(fetchCategoriesSuccess(categories)),
            dispatch(fetchVideosSuccess(videos)),
          ])
        } else {
          return Promise.all([
            dispatch(fetchCategoriesFailure(response.reason)),
            dispatch(fetchVideosFailure(response.reason)),
          ])
        }
      },
      () => {
        const errorMsg = getP(getState()).t('tv_video_library.categories_loading_error', {
          _: 'An error occurred while loading the list of video categories',
        })
        return Promise.all([
          dispatch(fetchCategoriesFailure(errorMsg)),
          dispatch(fetchVideosFailure(errorMsg)),
        ])
      }
    )
}

/**
 * Actions for fetching videos from API
 */
export const FETCH_VIDEOS_REQUEST = 'VIDEO_CATEGORY/FETCH/VIDEOS/REQUEST'
export const FETCH_VIDEOS_SUCCESS = 'VIDEO_CATEGORY/FETCH/VIDEOS/SUCCESS'
export const FETCH_VIDEOS_FAILURE = 'VIDEO_CATEGORY/FETCH/VIDEOS/FAILURE'

const fetchVideosRequest = () => ({
  type: FETCH_VIDEOS_REQUEST,
})

const fetchVideosSuccess = payload => ({
  type: FETCH_VIDEOS_SUCCESS,
  payload,
})

const fetchVideosFailure = error => ({
  type: FETCH_VIDEOS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadVideos = categoryId => (dispatch, getState) => {
  dispatch(fetchVideosRequest())
  return api
    .getCategoryVideos(categoryId)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const videos = []
          response.videos.forEach(video => {
            const processed = {
              id: video.id,
              name: video.name,
              desc: video.description,
              img: video.img.medium,
              duration: video.duration,
              created: video.created,
              isPremium: !!video.is_premium,
            }
            videos.push(processed)
          })
          return dispatch(fetchVideosSuccess(videos))
        } else {
          return dispatch(fetchVideosFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchVideosFailure(
            getP(getState()).t('tv_video_library.categories_loading_error', {
              _: 'An error occurred while loading the list videos for current category',
            })
          )
        )
    )
}

/**
 * Actions related to navigation
 */
export const SET_FOCUSED_SECTION = 'VIDEO_CATEGORY/LIST/SET_FOCUSED_SECTION'
export const SET_FOCUSED_POSITION = 'VIDEO_CATEGORY/LIST/SET_FOCUSED_POSITION'

export const setFocusedSection = focusedSection => ({
  type: SET_FOCUSED_SECTION,
  payload: focusedSection,
})
export const setFocusedPosition = (status, position) => {
  return {
    type: SET_FOCUSED_POSITION,
    payload: {
      status,
      position,
    },
  }
}

/**
 * Actions for lazy loading more videos
 */
export const FETCH_MORE_VIDEOS_REQUEST = 'VIDEO_CATEGORY/FETCH/MORE_VIDEOS/REQUEST'
export const FETCH_MORE_VIDEOS_SUCCESS = 'VIDEO_CATEGORY/FETCH/MORE_VIDEOS/SUCCESS'
export const FETCH_MORE_VIDEOS_FAILURE = 'VIDEO_CATEGORY/FETCH/MORE_VIDEOS/FAILURE'

const fetchMoreVideosRequest = () => ({
  type: FETCH_MORE_VIDEOS_REQUEST,
})

const fetchMoreVideosSuccess = payload => ({
  type: FETCH_MORE_VIDEOS_SUCCESS,
  payload,
})

const fetchMoreVideosFailure = error => ({
  type: FETCH_MORE_VIDEOS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadMoreVideos = (categoryId, offset) => (dispatch, getState) => {
  dispatch(fetchMoreVideosRequest())
  return api
    .getCategoryVideos(categoryId, offset, 24)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const moreVideos = []
          response.videos.forEach(video => {
            const processed = {
              id: video.id,
              name: video.name,
              desc: video.description,
              img: video.img.medium,
              duration: video.duration,
              created: video.created,
              isPremium: !!video.is_premium,
            }
            moreVideos.push(processed)
          })
          return dispatch(fetchMoreVideosSuccess(moreVideos))
        } else {
          return dispatch(fetchMoreVideosFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchMoreVideosFailure(
            getP(getState()).t('tv_video_library.category_videos_loading_error', {
              _: 'An error occurred while loading videos per category',
            })
          )
        )
    )
}

/**
 * Actions for fetching category videos per category from API
 */
export const FETCH_CATEGORY_VIDEOS_REQUEST =
  'VIDEO_CATEGORY/FETCH/CATEGORY_VIDEOS/REQUEST'
export const FETCH_CATEGORY_VIDEOS_SUCCESS =
  'VIDEO_CATEGORY/FETCH/CATEGORY_VIDEOS/SUCCESS'
export const FETCH_CATEGORY_VIDEOS_FAILURE =
  'VIDEO_CATEGORY/FETCH/CATEGORY_VIDEOS/FAILURE'

const fetchCategoryVideosRequest = categoryId => ({
  type: FETCH_CATEGORY_VIDEOS_REQUEST,
  payload: {
    categoryId,
  },
})

const fetchCategoryVideosSuccess = (categoryId, videos) => ({
  type: FETCH_CATEGORY_VIDEOS_SUCCESS,
  payload: {
    categoryId,
    videos,
  },
})

const fetchCategoryVideosFailure = (categoryId, error) => ({
  type: FETCH_CATEGORY_VIDEOS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    categoryId,
  },
  error: true,
})

export const loadCategoryVideos = categoryId => (dispatch, getState) => {
  dispatch(fetchCategoryVideosRequest())
  return api
    .getCategoryVideos(categoryId, 0, 12)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const moreVideos = []
          response.videos.forEach(video => {
            const processed = {
              id: video.id,
              name: video.name,
              desc: video.description,
              img: video.img.medium,
              duration: video.duration,
              created: video.created,
              isPremium: !!video.is_premium,
            }
            moreVideos.push(processed)
          })
          return dispatch(fetchCategoryVideosSuccess(categoryId, moreVideos))
        } else {
          return dispatch(fetchCategoryVideosFailure(categoryId, response.reason))
        }
      },
      () =>
        dispatch(
          fetchCategoryVideosFailure(
            categoryId,
            getP(getState()).t('tv_video_library.category_videos_loading_error', {
              _: 'An error occurred while loading videos per category',
            })
          )
        )
    )
}
